import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { Progress } from "@shared/ui/progress";
import { useMonthlySupportSummary } from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";

interface ServiceRequestSummaryProps {
  timeRange: "day" | "week" | "month" | "quarter" | "year";
  userRole: "manager" | "executive" | "callcenter" | "recovery";
}

interface ServiceCategoryProps {
  category: string;
  count: number;
  percentage: number;
  trend: "up" | "down" | "neutral";
}

function ServiceCategory({ category, count, percentage, trend }: ServiceCategoryProps) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs font-medium truncate">{category}</span>
        <div className="flex items-center gap-1">
          <span className="text-xs">{count}</span>
          <span className={`text-xs ${
            trend === 'up' ? 'text-red-500' : 
            trend === 'down' ? 'text-green-500' : 'text-gray-500'
          }`}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
          </span>
        </div>
      </div>
      <Progress value={percentage} className="h-1" />
    </div>
  );
}

export function ServiceRequestSummary({ supportData }) {
      const remoteUtilityId = getRemoteUtilityId();
  
  // Transform API data to service request data
  const serviceData = supportData ? {
    activeRequests: supportData.services.active,
    overdueRequests: supportData.services.overdue,
    completionTime: `${supportData.services.avgCompletionTimeHr} hrs`,
    firstTimeResolution: "0%", // This might need another API endpoint or calculation
    categories: transformServiceCategories(supportData.supportRequestTypeCounts?.Service || {}),
    severity: transformSeverityData(supportData.priorityCounts?.Service || {})
  } : {
    // Fallback data if API is not available
    activeRequests: 0,
    overdueRequests: 0,
    completionTime: "0 hrs",
    firstTimeResolution: "0%",
    categories: [],
    severity: [
      { level: "High", value: 0 },
      { level: "Medium", value: 0 },
      { level: "Low", value: 0 }
    ]
  };

  console.log("Service Data:", serviceData);  
  // Transform service categories from API response (top 5 only)
  function transformServiceCategories(serviceTypes: Record<string, number>) {
    const entries = Object.entries(serviceTypes);
    const totalCount = entries.reduce((sum, [, count]) => sum + count, 0);
    
    // Sort by count and take top 5
    const sortedEntries = entries
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return sortedEntries.map(([category, count]) => ({
      category: category.length > 20 ? category.substring(0, 17) + "..." : category,
      count,
      percentage: totalCount > 0 ? Math.round((count / totalCount) * 100) : 0,
      trend: "neutral" as const // Could be enhanced with historical data
    }));
  }

  // Transform severity data from API response
  function transformSeverityData(priorityCounts: Record<string, number>) {
    const totalCount = Object.values(priorityCounts).reduce((sum, count) => sum + count, 0);
    
    if (totalCount === 0) {
      return [
        { level: "High", value: 0 },
        { level: "Medium", value: 0 },
        { level: "Low", value: 0 }
      ];
    }

    // Map API priority levels to our severity levels
    const high = priorityCounts["High"] || priorityCounts["Critical"] || priorityCounts["Emergency"] || 0;
    const medium = priorityCounts["Medium"] || 0;
    const low = priorityCounts["Low"] || priorityCounts["Normal"] || 0;

    return [
      { level: "High", value: Math.round((high / totalCount) * 100) },
      { level: "Medium", value: Math.round((medium / totalCount) * 100) },
      { level: "Low", value: Math.round((low / totalCount) * 100) }
    ];
  }

  return (
   <Card className="h-full">
  <CardHeader className="">
    <CardTitle className="card-heading-text flex items-center justify-between">
      Service Requests
      <Clock className="h-4 w-4 text-muted-foreground" />
    </CardTitle>
  </CardHeader>
  <CardContent className="pt-2 space-y-3">
    {/* Active Requests & Overdue */}
    <div className="grid grid-cols-2 gap-2">
      <div className="flex flex-col items-center justify-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">Active Requests</span>
        <span className="card-value-text font-bold text-blue-600">
          {serviceData.activeRequests}
        </span>
      </div>
      <div className="flex flex-col items-center justify-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">Overdue</span>
        <span className="card-value-text font-bold text-red-600">
          {serviceData.overdueRequests}
        </span>
      </div>
    </div>

    {/* Avg Completion & First Resolution */}
    <div className="grid grid-cols-2 gap-2">
      <div className="flex flex-col items-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">Avg Completion</span>
        <span className="card-value-text font-bold">
          {serviceData.completionTime}
        </span>
      </div>
      <div className="flex flex-col items-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">First Resolution</span>
        <span className="card-value-text font-bold">
          {serviceData.firstTimeResolution}
        </span>
      </div>
    </div>

    {/* By Category */}
    <div className="space-y-1.5">
      <h3 className="card-subheading-text font-medium text-muted-foreground">By Category</h3>
      <div className="space-y-2">
        {serviceData.categories.map((item) => (
          <ServiceCategory key={item.category} {...item} />
        ))}
      </div>
    </div>

    {/* By Severity */}
    <div className="space-y-1">
      <h3 className="card-subheading-text font-medium text-muted-foreground">By Severity</h3>
      <TooltipProvider>
        <div className="flex items-center gap-0.5 h-3">
          {serviceData.severity.map((level, index) => (
            <Tooltip key={level.level}>
              <TooltipTrigger asChild>
                <div
                  style={{ width: `${level.value}%` }}
                  className={`h-full rounded ${
                    index === 0
                      ? "bg-red-500"
                      : index === 1
                      ? "bg-amber-400"
                      : "bg-green-400"
                  }`}
                />
              </TooltipTrigger>
              <TooltipContent className="text-xs" side="top">
                {level.level}: {level.value}%
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>High</span>
        <span>Medium</span>
        <span>Low</span>
      </div>
    </div>
  </CardContent>
</Card>

  );
}